import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import { sugarService, type Sugar } from "../services/sugar";

type SugarSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useSugar() {
  const { t } = useI18n();
  const sugars = ref<Sugar[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedSugar = ref<Sugar | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Sugar>({
    code: "",
    name: "",
    description: "",
    status: 1,
  });

  const fetchSugars = async () => {
    loading.value = true;
    try {
      sugars.value = await sugarService.getSugars();
    } catch (error) {
      console.error("Failed to fetch preparation options:", error);
      Swal.fire({
        icon: "error",
        title: t("sugar_page.load_failed"),
        text: t("sugar_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredSugars = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return sugars.value;

    return sugars.value.filter((sugar) =>
      [sugar.code, sugar.name, sugar.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const stats = computed(() => {
    const total = sugars.value.length;
    const active = sugars.value.filter(
      (sugar) => String(sugar.status ?? 1) !== "0" && String(sugar.status ?? 1) !== "false",
    ).length;

    return {
      total,
      active,
      inactive: Math.max(total - active, 0),
    };
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("sugar_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("sugar_page.items_per_page") },
  }));

  watch(
    filteredSugars,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      description: "",
      status: 1,
    };
    errors.value = {};
  };

  const openCreateModal = () => {
    selectedSugar.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (sugar: Sugar) => {
    selectedSugar.value = sugar;
    errors.value = {};
    form.value = {
      id: sugar.id,
      code: sugar.code || "",
      name: sugar.name || "",
      description: sugar.description || "",
      status: String(sugar.status) === "0" || String(sugar.status) === "false" ? 0 : 1,
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = (field: keyof Sugar, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const generateSugarCode = async () => {
    try {
      if (!sugars.value.length) {
        await fetchSugars();
      }

      const nextNumber =
        sugars.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `SG${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate preparation code:", error);
      Swal.fire({
        icon: "error",
        title: t("sugar_page.generate_failed"),
        text: t("sugar_page.generate_failed_text"),
      });
    }
  };

  const saveSugar = async () => {
    loading.value = true;
    errors.value = {};
    try {
      const payload = {
        code: form.value.code || "",
        name: form.value.name || "",
        description: form.value.description || undefined,
        status: Number(form.value.status ?? 1),
      };

      if (selectedSugar.value?.id) {
        await sugarService.updateSugar(selectedSugar.value.id, payload);
      } else {
        await sugarService.createSugar(payload);
      }

      await fetchSugars();
      await Swal.fire({
        icon: "success",
        title: selectedSugar.value ? t("sugar_page.updated_title") : t("sugar_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as SugarSaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const message =
          requestError.response?.data?.message ||
          Object.values(requestError.response?.data?.errors || {}).flat()[0] ||
          t("sugar_page.save_failed_text");
        Swal.fire({
          icon: "error",
          title: t("sugar_page.save_failed"),
          text: message as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteSugar = async (sugar: Sugar) => {
    if (!sugar?.id) return;

    const result = await Swal.fire({
      title: t("sugar_page.delete_title"),
      text: t("sugar_page.delete_text", { name: sugar.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("sugar_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("sugar_page.deleting_title"),
        text: t("sugar_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await sugarService.deleteSugar(sugar.id);
      await fetchSugars();

      Swal.fire({
        icon: "success",
        title: t("sugar_page.deleted_title"),
        text: t("sugar_page.deleted_text", { name: sugar.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("sugar_page.delete_failed"),
        text: t("sugar_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredSugars,
    stats,
    showModal,
    selectedSugar,
    form,
    errors,
    loading,
    paginationConfig,
    fetchSugars,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    generateSugarCode,
    saveSugar,
    deleteSugar,
  };
}
